import { useContext, useEffect } from "react"
import { Link } from "react-router-dom"
import FieldBuilder from "./FieldBuilder"
import BlockActionBtns from "./BlockActionBtns"
import FormSummary from "./components/FormSummary"
import { FormContext } from "../../context/formContext"
import { useFormikContext } from "formik"
import { DateTime } from "luxon"
import { transformDate } from "../../helpers/dateNormalization"
import ROUTES from "../../components/Routes/routesContants"

// Custom Blocks
import Assessments from "./CustomBlocks/Assessments"

import styles from "../../styles/Form.module.css"

export default function BlockBuilder({ block }) {
  const form = useContext(FormContext)
  const formik = useFormikContext()
  const isSubmitBlock = block?.block_name === "submit_block"
  const isCustomAssessmentBlock =
    block.block_name === "session__assessment" ||
    block.block_name === "session__assessment____custom"

  // Determine if the block is propagation-locked based on its configuration and edit mode
  const isPropagationLockedBlock =
    Boolean(form.editMode) &&
    !Boolean(form.isLatestSession) &&
    !isSubmitBlock &&
    block?.configuration?.behavior?.data_propagation === true

  // Get the session date to display in the propagation lock notice if applicable
  const getNoticeSessionDate = () => {
    const rawValue = form?.propagationNoticeDateRaw
    if (!rawValue) return ""

    const parsed = transformDate(rawValue, "date", "date", true)

    if (parsed?.isValid) {
      return parsed.toLocaleString(DateTime.DATE_MED)
    }

    // Fall back to raw text so the notice still shows something diagnostic.
    return String(rawValue)
  }

  const noticeSessionDate = isPropagationLockedBlock
    ? getNoticeSessionDate()
    : ""
  const noticeSessionDateLabel = noticeSessionDate || "UNKNOWN"

  /* Monitor loading fields to reset form dirty state */
  useEffect(() => {
    // If not empty and all fields are loaded, mark block as loaded
    const loadingVals = Object.values(form.loadingFields || {})
    if (loadingVals.length > 0 && !loadingVals.some((v) => v === true)) {
      // All registered fields are loaded
      formik.resetForm({ values: formik.values }) // Reset dirty state
      form.clearLoadingFields() // Clear loading fields
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.loadingFields])

  if (isCustomAssessmentBlock) {
    return <Assessments block={block} />
  }

  const renderActionButtons = (style) => {
    if (isSubmitBlock) return null
    return (
      <div className={style}>
        <BlockActionBtns block={block.block_name} />
      </div>
    )
  }

  return (
    <div key={block.block_name} className={styles.block}>
      <div className={styles.blockHeaderLayout}>
        <div className={styles.blockHeader}>
          <h2 className={styles.blockTitle}>{block.block_title}</h2>
          <p>
            {!isSubmitBlock && (
              <>
                <span className={styles.asterisk}>* </span>indicates required
                field{" "}
              </>
            )}
          </p>
        </div>
      </div>

      {isPropagationLockedBlock && (
        <div className={`${styles.propagationLockNotice} notice noticeInfo`}>
          <p>
            These are the <strong>{block.block_title}</strong> values collected{" "}
            on <strong>{noticeSessionDateLabel}</strong>. Updates should be made
            in a new session.
          </p>
        </div>
      )}

      {renderActionButtons(styles.blockActions)}

      {/* Render normal block */}
      {!isSubmitBlock && (
        <FieldBuilder fields={block.fields} block={block.block_name} />
      )}

      {/* Render submit block */}
      {isSubmitBlock && <FormSummary />}

      {renderActionButtons(styles.blockActions)}
    </div>
  )
}
